import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
  Component,
  ViewChild,
  OnInit,
  OnDestroy,
  effect,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { fromEvent, Subject } from 'rxjs';
import { distinctUntilKeyChanged, take, takeUntil } from 'rxjs/operators';
import { AccountSettingsService } from 'src/app/core/account-settings.service';
import { AlertService } from 'src/app/core/alert.service';
import { BrowserNotificationService } from 'src/app/core/browser-notification.service';
import { PopupService } from 'src/app/core/popup.service';
import { SidenavDrawerService } from 'src/app/core/sidenav-drawer.service';
import { SignalRService } from 'src/app/core/signalR-service';
import { UserService } from 'src/app/core/user.service';
import { environment } from 'src/environments/environment';
import { TermsGeneric } from 'src/helpers';
import {
  AlertServiceType,
  AlertType,
  BrowserNotification,
  User,
} from 'src/models';
import { NotificationMenuContainerComponent } from '../notification-menu-container/notification-menu-container.component';
import { NotificationPushMenuContainerComponent } from '../notification-push-menu-container/notification-push-menu-container.component';
import { UserAvatarComponent } from 'src/app/shared/user-avatar/user-avatar.component';
import { ClickOutsideModule } from 'ng-click-outside';
import { RouterModule } from '@angular/router';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RightClickMenuDirective } from 'src/helpers/directives/right-click-menu/right-click-menu.directive';

/**
 * Component for the top site navigation.
 */
@Component({
  selector: 'app-top-nav',
  templateUrl: './top-nav.component.html',
  styleUrls: ['./top-nav.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatMenuModule,
    NotificationMenuContainerComponent,
    NotificationPushMenuContainerComponent,
    UserAvatarComponent,
    ClickOutsideModule,
    RouterModule,
    MatSidenavModule,
    MatSlideToggleModule,
    MatTooltipModule,
    NgOptimizedImage,
    RightClickMenuDirective,
  ],
})
export class TopNavComponent implements OnInit, OnDestroy {
  /** Trigger for user menu. */
  @ViewChild(MatMenuTrigger)
  private userMenuTrigger!: MatMenuTrigger;

  /** Observable for when the component is destroyed. */
  private destroyed$ = new Subject<void>();

  /**
   * Whether realtime and the alert fetch have already been started. Needed because the token signal
   * changes on every refresh-token rotation (roughly every 15 minutes), and neither the hub
   * connection nor the alert list should be re-established on those.
   */
  private realtimeStarted = false;

  /** Whether the signed in user is an isArchitect or not. */
  isArchitect = false;

  /** User variable. */
  user!: User;

  /** Init the browser width. */
  innerWidth = 0;

  /** Use to toggle visibility of notifications. */
  notificationsVisible = false;

  /** The subject data for updated user first name and last name. */
  sub$ = new Subject();

  /** How many update has been done, this is to avoid an infinite loop. */
  tryUpdateUserConfiguration = 0;

  /** Use to indicate new notification. */
  newNotification = false;

  //** Enable Push Notification component visible.*/
  pushNotificationsVisible = false;

  /** Generic terms. */
  termsGeneric = TermsGeneric;

  constructor(
    public sidenavDrawerService: SidenavDrawerService,
    private userService: UserService,
    private accountSettingsService: AccountSettingsService,
    private signalRService: SignalRService,
    private alertService: AlertService,
    private popupService: PopupService,
    private browserNotification: BrowserNotificationService,
    private dialog: MatDialog,
  ) {
    this.onResize();

    // Start realtime as soon as a token EXISTS, not when this component mounts. The two are not the
    // same moment: this component lives in the persistent shell (app.component.html, outside the
    // router outlet), so `ngOnInit` runs ONCE — on whichever route first turns `showTopNav` on. After
    // an SSO login that route is the OIDC callback, which renders before the ticket has been
    // exchanged for a session, so the one-shot check below found no token, and nothing ever retried:
    // the hub stayed unconnected (no negotiate at all) until a full page reload happened to mount it
    // with a token already in hand. Reacting to the signal covers every way a token can arrive —
    // password sign-in, SSO exchange, refresh-token restore — regardless of mount order.
    effect(() => {
      const token = this.userService.accessToken$();
      if (!token || token.isExpired() || this.realtimeStarted) {
        return;
      }
      this.realtimeStarted = true;
      this.signalRService.connect();
      this.getAlerts();
    });
  }

  /** Subscribe to signalR notification event. */
  private subscribeNewNotification(): void {
    this.signalRService.receivedAlert$
      .pipe(distinctUntilKeyChanged('rithmId'), takeUntil(this.destroyed$))
      .subscribe((data) => {
        if (data && data.type === AlertType.Browser) {
          if (this.alertService.checkAlertInAlertList(data.rithmId)) return;
          const {
            stationRithmId,
            containerRithmId,
            stationName,
            containerName,
            note,
          } = data;
          const notificationMessage =
            data.service === AlertServiceType.Station
              ? `${stationName}\n${containerName}\n${note}`
              : note;
          // eslint-disable-next-line max-len
          const notificationUrl = `${environment.baseAppUrl}/${TermsGeneric.Container.Single.toLowerCase()}/${containerRithmId}?containerId=${containerRithmId}&stationId=${stationRithmId}`;

          const browserAlert: BrowserNotification = {
            title: TermsGeneric.NameApp,
            gotoURL: notificationUrl,
            bodyContent: notificationMessage,
            imgURL: `${environment.baseAppUrl}/assets/favicons/default_UAT/favicon-196x196.png`,
          };
          this.browserNotification.showNotification(browserAlert);
        }

        if (
          data.type === AlertType.Silent ||
          (data.type === AlertType.Push && !data.hasRead)
        ) {
          this.newNotification = true;
        }
      });
  }

  /**
   * Sets updated user first name and last name in account settings component.
   *
   */
  ngOnInit(): void {
    // Setup...
    this.pushNotificationsVisible = true;
    this.user = this.userService.user();
    this.isArchitect = this.userService.isAdmin();
    this.accountSettingsService.currentUser$
      .pipe(takeUntil(this.sub$))
      .subscribe((user) => {
        if (
          user &&
          user.firstName !== null &&
          user.lastName !== null &&
          user.profileImageRithmId !== null
        ) {
          this.user.firstName = user?.firstName;
          this.user.lastName = user?.lastName;
          this.user.profileImageRithmId = user?.profileImageRithmId;
        }
      });

    // Kept alongside the token effect in the constructor, for two reasons: it is what DRIVES the
    // refresh-token restore on a page reload (the effect only reacts once a token exists, it cannot
    // cause one), and it covers the case where the token was already in hand before this component
    // mounted. Both paths funnel into the same guard, and `connect()` is idempotent, so whichever
    // arrives first wins and the second is a no-op.
    //
    // The original reason for gating on the token stands: acting before it exists makes the worker
    // negotiate without a Bearer token (400 "Bearer token missing") and fires getAlerts during the
    // first-login navigation churn (cancelled/empty stream -> spurious error toast).
    this.userService.isSignedIn().then((signedIn) => {
      if (signedIn && !this.realtimeStarted) {
        this.realtimeStarted = true;
        this.signalRService.connect();
        this.getAlerts();
      }
    });
    this.subscribeNewNotification();
    this.subscribeOnSignOut();
  }

  /**
   * Toggle opening and closing of the mobile navigation.
   */
  toggle(): void {
    this.sidenavDrawerService.toggleSidenav();
  }

  /**
   * Close sidenavDrawer menu when only is mobile and is open.
   */
  closeMobileSidenav(): void {
    this.sidenavDrawerService.closeSidenav();
  }

  /**
   * Check if screen size changes to hide mobile nav.
   * Close user menu if screen size changes.
   */
  onResize(): void {
    fromEvent(window, 'resize')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.innerWidth = window.innerWidth;

        if (this.innerWidth >= 768) {
          this.closeMobileSidenav();
        }
        if (this.innerWidth <= 768) {
          this.userMenuTrigger.closeMenu();
        }
      });
  }

  /**
   * Toggle opening and closing of notifications.
   */
  toggleNotifications(): void {
    this.notificationsVisible = !this.notificationsVisible;
    this.newNotification = false;
  }

  /**
   * Check if notifications are open before closing them.
   */
  clickedOutside(): void {
    if (this.notificationsVisible) {
      this.notificationsVisible = false;
      this.newNotification = false;
    }
  }

  /**
   * Signs the user out of the app.
   */
  signOut(): void {
    this.sidenavDrawerService.isSideNavOpen() &&
      this.sidenavDrawerService.closeSidenav();
    if (this.dialog.openDialogs.length) {
      this.dialog.closeAll();
    }
    this.userService.onBeforeSignOut$.next();
  }

  /**
   * Update profile Image.
   */
  updateProfileImage(): void {
    this.tryUpdateUserConfiguration++;
    if (this.tryUpdateUserConfiguration < 3) {
      this.userService.updateUserData();
    }
  }

  /**
   * Gets a list of alerts.
   */
  private getAlerts(): void {
    const includeRead = false;
    this.alertService
      .getAlerts(includeRead)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          if (data && data.length > 0 && data.some((alert) => !alert.hasRead)) {
            this.newNotification = true;
          }
        },
        error: () => {
          this.popupService.notify(
            "Something went wrong on our end and we're looking into it. Please try again in a little while. (ref: TOPNAV)",
            true,
          );
        },
      });
  }

  /**
   * Subscribe to signs the user out of the app.
   */
  private subscribeOnSignOut(): void {
    this.userService.onSignOut$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        // User-initiated logout: also end the UserAX SSO session.
        this.userService.signOut(false, undefined, true);
      },
    });
  }

  /**
   * Completes all subscriptions.
   */
  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.signalRService.onDestroy();
  }
}
